/** @jsxRuntime automatic */
/** @jsxImportSource react */

export type SparklineTone = 'data' | 'accent' | 'ok' | 'warn' | 'bad';

export interface SparklineProps {
  /** One bar per value, oldest first; negative values draw as empty bars. */
  values: readonly number[];
  /** What the bars count, for assistive tech ("join selected walls: asks per day, last 7 days"). */
  label: string;
  /** `data` (the default) for a count; `bad` for a series that is going wrong. */
  tone?: SparklineTone;
  /** The value a full-height bar stands for; the tallest value when absent. */
  max?: number;
  className?: string;
}

const BAR_W = 4;
const GAP = 2;
const HEIGHT = 16;

/**
 * A tiny bar chart in a table cell (a 7-day trend, 24 hours of calls). Pure SVG: the bars' sizes are attributes, not
 * styles, and their colour is a kit token (kit.css), so a screen draws one without a chart library or an inline style.
 */
export function Sparkline({ values, label, tone = 'data', max, className }: SparklineProps) {
  const top = Math.max(max ?? 0, ...values.map((value) => (Number.isFinite(value) ? value : 0)), 1);
  const width = Math.max(values.length * (BAR_W + GAP) - GAP, BAR_W);
  return (
    <svg
      className={['ui-spark', `ui-spark--${tone}`, className ?? ''].filter(Boolean).join(' ')}
      role="img"
      aria-label={label}
      width={width}
      height={HEIGHT}
      viewBox={`0 0 ${width} ${HEIGHT}`}
    >
      {values.map((value, index) => {
        const share = Number.isFinite(value) && value > 0 ? Math.min(1, value / top) : 0;
        const height = Math.max(1, Math.round(share * HEIGHT));
        return <rect key={index} x={index * (BAR_W + GAP)} y={HEIGHT - height} width={BAR_W} height={height} rx={1} className={share === 0 ? 'is-empty' : undefined} />;
      })}
    </svg>
  );
}
